import { useFrame, useLoader } from '@react-three/fiber';
import {
  forwardRef,
  MutableRefObject,
  RefObject,
  Suspense,
  useImperativeHandle,
  useRef,
} from 'react';
import { Group, TextureLoader } from 'three';

import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';

import AudioInteractiveMaterial, {
  AudioInteractiveMaterialRef,
} from './customMaterials/AudioInteractiveMaterial/AudioInteractiveMaterial';

const TEXTURE_PATH = publicAssets('/audioVisualizer/texture.png');
// const SHAPE_PATH = publicAssets('audioVisualizer/shape.png');
// const GLOW_PATH = publicAssets('audioVisualizer/glow.png');
const OUTLINE_PATH = publicAssets('/audioVisualizer/outline.png');

export interface AudioVisualizerProps extends GroupProps {
  initialOpacity?: number;
}

export interface AudioVisualizerRef {
  audioVisualizer: RefObject<Group>;
  opacity: MutableRefObject<number>;
}

const AudioVisualizer = forwardRef<AudioVisualizerRef, AudioVisualizerProps>(
  ({ initialOpacity = 1.0, ...rest }, ref) => {
    const audioVisualizerRef = useRef<Group>(null!);
    const audioInteractiveMaterialRef = useRef<AudioInteractiveMaterialRef>(
      null!
    );
    const opacityRef = useRef<number>(initialOpacity);

    const [texture, /* shape, */ outline] = useLoader(TextureLoader, [
      TEXTURE_PATH,
      /* SHAPE_PATH, */
      // GLOW_PATH,
      OUTLINE_PATH,
    ]);

    useFrame(() => {
      audioInteractiveMaterialRef.current.opacity.value = opacityRef.current;
      // trimmedBlurredMaterialRef.current.opacity.value = opacityRef.current;
    });

    useImperativeHandle(ref, () => ({
      audioVisualizer: audioVisualizerRef,
      opacity: opacityRef,
    }));

    return (
      <group {...rest} ref={audioVisualizerRef} frustumCulled={false}>
        <Suspense fallback={null}>
          <mesh scale={[-6.2, 6.2, 6.2]} frustumCulled={false}>
            <planeGeometry args={[1, 1]} />
            <AudioInteractiveMaterial
              outline={outline}
              texture={texture}
              ref={audioInteractiveMaterialRef}
              initialOpacity={initialOpacity}
            />
          </mesh>
        </Suspense>
      </group>
    );
  }
);

AudioVisualizer.displayName = 'AudioVisualizer';
export default AudioVisualizer;
